import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Stack,
  Grid,
  Divider,
  Chip,
  Alert,
} from '@mui/material';
import { Schedule, Send, Save, ListAlt } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

export interface QueueSettings {
  message_interval_minutes: number;
  max_messages_per_hour: number;
  enable_scheduling: boolean;
  working_hours_start: string;
  working_hours_end: string;
  priority_interval_minutes: Record<1 | 2 | 3 | 4 | 5, number>;
}

interface Props {
  settings: QueueSettings;
  loading: boolean;
  onChange: (settings: QueueSettings) => void;
  onSave: () => void;
  onProcessQueue: () => void;
}

const PRIORITY_LABELS: Record<number, string> = {
  1: 'Tinggi — konfirmasi pembayaran',
  2: 'Normal — reminder tagihan',
  3: 'Rendah',
  4: 'Sangat rendah',
  5: 'Terendah — broadcast',
};

const WhatsappQueuePanel: React.FC<Props> = ({
  settings,
  loading,
  onChange,
  onSave,
  onProcessQueue,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Antrian & Penjadwalan
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Atur interval pengiriman agar tidak kena limit WhatsApp / StarSender.
          </Typography>
        </Box>
        <Chip
          icon={<Schedule />}
          label={settings.enable_scheduling ? 'Penjadwalan aktif' : 'Kirim langsung'}
          color={settings.enable_scheduling ? 'success' : 'warning'}
          size="small"
        />
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
            Pengaturan umum
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enable_scheduling}
                  onChange={(e) => onChange({ ...settings, enable_scheduling: e.target.checked })}
                />
              }
              label="Aktifkan antrian terjadwal"
            />
            <TextField
              type="number"
              label="Interval default (menit)"
              value={settings.message_interval_minutes}
              onChange={(e) => onChange({ ...settings, message_interval_minutes: Number(e.target.value) })}
              size="small"
              fullWidth
              inputProps={{ min: 0.1, step: 0.1 }}
            />
            <TextField
              type="number"
              label="Maks. pesan per jam"
              value={settings.max_messages_per_hour}
              onChange={(e) => onChange({ ...settings, max_messages_per_hour: Number(e.target.value) })}
              size="small"
              fullWidth
              inputProps={{ min: 1 }}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="Jam mulai"
                type="time"
                value={settings.working_hours_start}
                onChange={(e) => onChange({ ...settings, working_hours_start: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Jam selesai"
                type="time"
                value={settings.working_hours_end}
                onChange={(e) => onChange({ ...settings, working_hours_end: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
            Interval per prioritas (menit)
          </Typography>
          <Stack spacing={1.5}>
            {([1, 2, 3, 4, 5] as const).map((p) => (
              <TextField
                key={p}
                type="number"
                label={`Prioritas ${p}`}
                value={settings.priority_interval_minutes[p]}
                onChange={(e) =>
                  onChange({
                    ...settings,
                    priority_interval_minutes: {
                      ...settings.priority_interval_minutes,
                      [p]: Number(e.target.value),
                    },
                  })
                }
                size="small"
                fullWidth
                helperText={PRIORITY_LABELS[p]}
                inputProps={{ min: 0.1, step: 0.1 }}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap">
        <Button variant="contained" onClick={onSave} disabled={loading} startIcon={<Save />}>
          Simpan pengaturan
        </Button>
        <Button variant="outlined" onClick={onProcessQueue} disabled={loading} startIcon={<Send />}>
          Proses antrian sekarang
        </Button>
        <Button
          component={RouterLink}
          to="/whatsapp/queue/log"
          variant="text"
          startIcon={<ListAlt />}
        >
          Lihat log antrian
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mt: 2 }}>
        Jam operasional: <strong>{settings.working_hours_start} – {settings.working_hours_end}</strong> WIB.
        Pesan di luar jam ini dijadwalkan ke hari berikutnya. Scheduler server memproses antrian setiap 10 detik.
      </Alert>
    </CardContent>
  </Card>
);

export default WhatsappQueuePanel;
